"use client";

import { Settings } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";

import { Comedian } from "@punchup/data";
import {
  comedianPlanBillingMenuItems,
  comedianSettingsMenuItems,
} from "./useDashboardMenuItems";
import { type DashboardSettingsNav } from "./types";
import {
  rememberSettingsReturnPath,
  takeSettingsReturnPath,
} from "./settingsReturnPath";

const SETTINGS_LABEL = "Settings";

interface UseComedianSettingsNavOptions {
  comedianSlug: string;
  selectedComedian: Comedian | null;
  /** The settings gate's answer. The caller reads it; this package reads no gate. */
  enabled: boolean;
  /** Lists Plan & usage and Billing; the caller reads the Automations gate. */
  planBilling?: boolean;
}

/**
 * The comedian dashboard's settings pages, and the way the nav shows them.
 * `undefined` when `enabled` is false.
 *
 * It always returns the "Settings" footer row, which leads to the first settings
 * page. On a settings page it also returns the rail's drilldown, which hides the
 * footer. Its Back button returns to the dashboard page the user opened Settings
 * from, or to the dashboard root when there is none. Both are derived from the route on every render.
 * `sessionStorage` is read and written in click handlers only, so the server and
 * the client render the same markup.
 *
 * Each `label` is a frozen Mixpanel key, the same one the Profile group's rows
 * report, so a click reads the same in both navs.
 */
export function useComedianSettingsNav({
  comedianSlug,
  selectedComedian,
  enabled,
  planBilling = false,
}: UseComedianSettingsNavOptions): DashboardSettingsNav | undefined {
  const pathname = usePathname();
  const router = useRouter();

  if (!enabled) return undefined;

  const slug = selectedComedian?.slug ?? comedianSlug;
  const items = [
    ...comedianSettingsMenuItems,
    ...(planBilling ? comedianPlanBillingMenuItems : []),
  ].map(({ path, subitems: _subitems, ...item }) => {
    const url = `/${slug}${path}`;
    return { ...item, url, active: pathname === url };
  });

  const inSettings = items.some(({ active }) => active);
  // Back reports the footer row's label too, so only a click outside Settings keeps the page.
  const onItemClick: DashboardSettingsNav["onItemClick"] = ({ item }) => {
    if (!inSettings && item === SETTINGS_LABEL) {
      rememberSettingsReturnPath(slug, pathname);
    }
  };

  return {
    items,
    onItemClick,
    entry: {
      label: SETTINGS_LABEL,
      title: "Settings",
      url: items[0].url,
      Icon: Settings,
      active: false,
    },
    drilldown: inSettings
      ? {
          label: SETTINGS_LABEL,
          title: "Settings",
          onBack: () => router.push(takeSettingsReturnPath(slug)),
          items,
        }
      : undefined,
  };
}
